import { useCallback, useRef, type MouseEvent } from 'react';
import type { Size } from './markupGeometry';
import { hitOptionsFor, topmostHit } from './markupHitTest';
import type { MarkupShape, MarkupTool, TextShape } from './markupShapes';
import { fontSizeFor, type MarkupSize } from './markupStyle';
import { measureLabel } from './textMeasure';
import { toImagePoint } from './useMarkupPointer';
import type { TextDraftControls } from './useTextDraft';

interface UseMarkupTextClicksArgs {
    tool: MarkupTool;
    color: string;
    size: MarkupSize;
    image: Size;
    scale: number;
    shapes: readonly MarkupShape[];
    text: TextDraftControls;
    onSelect: (shape: MarkupShape | null) => void;
}

type CanvasMouseEvent = MouseEvent<HTMLCanvasElement>;

export interface MarkupTextClicks {
    onPress: () => void;
    onClick: (e: CanvasMouseEvent) => void;
    onDoubleClick: (e: CanvasMouseEvent) => void;
}

export function useMarkupTextClicks({
    tool,
    color,
    size,
    image,
    scale,
    shapes,
    text,
    onSelect
}: UseMarkupTextClicksArgs): MarkupTextClicks {
    // A press that closed an open label must not also drop a new one.
    const skipNextClickRef = useRef(false);

    const labelAt = useCallback(
        (e: CanvasMouseEvent): { at: ReturnType<typeof toImagePoint>; label: TextShape | null } => {
            const at = toImagePoint(e, e.currentTarget, scale, image);
            const hit = topmostHit(shapes, at, hitOptionsFor(scale), measureLabel);
            return { at, label: hit?.kind === 'text' ? hit : null };
        },
        [scale, image, shapes]
    );

    const onPress = useCallback(() => {
        skipNextClickRef.current = tool === 'text' && text.draft != null;
        text.commit();
    }, [tool, text]);

    // Placed on click, after a touch's compatibility mouse events, so the new field keeps focus.
    const onClick = useCallback(
        (e: CanvasMouseEvent) => {
            if (tool !== 'text') return;
            if (skipNextClickRef.current) {
                skipNextClickRef.current = false;
                return;
            }
            const { at, label } = labelAt(e);
            if (label) text.edit(label);
            else text.place(at, color, fontSizeFor(size, image));
        },
        [tool, labelAt, text, color, size, image]
    );

    const onDoubleClick = useCallback(
        (e: CanvasMouseEvent) => {
            if (tool !== 'select') return;
            const { label } = labelAt(e);
            if (!label) return;
            onSelect(label);
            text.edit(label);
        },
        [tool, labelAt, onSelect, text]
    );

    return { onPress, onClick, onDoubleClick };
}
